<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

type SelectSize = 'sm' | 'md' | 'lg'
type SelectVariant = 'outlined' | 'compact'

interface DropdownOption {
  [key: string]: unknown
}

const props = withDefaults(defineProps<{
  modelValue?: string | number | null
  options?: DropdownOption[]
  valueField?: string
  labelField?: string
  /** 触发框短文案字段：选中后触发框显示短文案、弹层列表仍显示完整 label。
   *  自绘组件直接取字段渲染，无原生 select 的 label 属性时序问题 */
  selectedLabelField?: string
  badgeField?: string
  placeholder?: string
  searchable?: boolean
  searchPlaceholder?: string
  disabled?: boolean
  /** 校验错误态：红色描边，对齐表单控件规范 */
  error?: boolean
  /** 尺寸：高度/字号与表单控件规范一致（sm=36 / md=48 / lg=56） */
  size?: SelectSize
  /** 外观：outlined=表单控件（默认）；compact=紧凑工具条/筛选区 */
  variant?: SelectVariant
  clearable?: boolean
  width?: string | number
  align?: 'left' | 'right'
  emptyText?: string
}>(), {
  modelValue: null,
  options: () => [],
  valueField: 'value',
  labelField: 'label',
  selectedLabelField: 'selectedLabel',
  badgeField: '',
  placeholder: '请选择',
  searchable: false,
  searchPlaceholder: '搜索...',
  disabled: false,
  error: false,
  size: 'md',
  variant: 'outlined',
  clearable: false,
  width: '100%',
  align: 'right',
  emptyText: '无匹配项'
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null]
  'change': [value: string | number | null]
}>()

const PANEL_GAP_PX = 4
const PANEL_MAX_PX = 300
const PANEL_MIN_PX = 96
const VIEWPORT_PAD_PX = 8
const OPTION_ESTIMATE_PX = 44

const dropdownOpen = ref(false)
const searchKeyword = ref('')
const wrapperRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)
/** 弹层列表的实时高度上限（px）；null = 用 CSS 默认 300px */
const listMaxHeight = ref<number | null>(null)
const panelPositionStyle = ref<Record<string, string>>({})

/** 弹层 Teleport 到 body + position:fixed：避开 Modal/Drawer 等 overflow 祖先裁切。
 *  打开前先按触发框量一次，避免首帧落到视口底部。 */
function updatePanelPosition(): void {
  const trigger = wrapperRef.value?.querySelector<HTMLElement>('.dropdown-select__trigger')
  if (!trigger) return
  const rect = trigger.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom - PANEL_GAP_PX - VIEWPORT_PAD_PX
  const spaceAbove = rect.top - PANEL_GAP_PX - VIEWPORT_PAD_PX
  const estimated = Math.min(
    PANEL_MAX_PX,
    Math.max(PANEL_MIN_PX, filteredOptions.value.length * OPTION_ESTIMATE_PX + 8)
  )
  const placeAbove = spaceBelow < estimated && spaceAbove > spaceBelow
  const available = placeAbove ? spaceAbove : spaceBelow
  listMaxHeight.value = Math.max(PANEL_MIN_PX, Math.floor(Math.min(PANEL_MAX_PX, available)))

  const style: Record<string, string> = {
    minWidth: `${Math.round(rect.width)}px`
  }
  if (placeAbove) {
    style.top = 'auto'
    style.bottom = `${Math.round(window.innerHeight - rect.top + PANEL_GAP_PX)}px`
  } else {
    style.top = `${Math.round(rect.bottom + PANEL_GAP_PX)}px`
    style.bottom = 'auto'
  }
  if (props.align === 'left') {
    style.left = `${Math.round(rect.left)}px`
    style.right = 'auto'
  } else {
    style.left = 'auto'
    style.right = `${Math.round(window.innerWidth - rect.right)}px`
  }
  panelPositionStyle.value = style
}

/** 打开弹层后把当前选中项滚动到可见区域：弹层关闭即移出 DOM，滚动位置无法跨开关保留，
 *  长列表（教师/班级课表切换）每次重开都从顶部开始，用户需重新滚动定位。
 *  优先让选中项完整可见；列表高度装不下整项时退化为就近边界对齐 */
async function scrollToSelectedOption(): Promise<void> {
  await nextTick()
  const list = listRef.value
  if (!list) return
  const selected = list.querySelector<HTMLElement>('.dropdown-panel__option--selected')
  if (!selected) return
  const fullyVisible = selected.offsetHeight <= list.clientHeight
  selected.scrollIntoView({ block: fullyVisible ? 'nearest' : 'start' })
}

function getValue(option: DropdownOption): unknown {
  return option[props.valueField]
}

function getLabel(option: DropdownOption): string {
  return String(option[props.labelField] ?? '')
}

/** 触发框展示文案：优先取短文案字段，缺省回退完整 label */
function getSelectedLabel(option: DropdownOption): string {
  const short = option[props.selectedLabelField]
  return short == null || short === '' ? getLabel(option) : String(short)
}

function getBadge(option: DropdownOption): string | null {
  if (!props.badgeField) return null
  const badge = option[props.badgeField]
  return badge == null || badge === '' ? null : String(badge)
}

const selectedOption = computed(
  () => props.options.find((o) => getValue(o) === props.modelValue) ?? null
)

const filteredOptions = computed(() => {
  if (!props.searchable || !searchKeyword.value.trim()) return props.options
  const keyword = searchKeyword.value.trim().toLowerCase()
  return props.options.filter((o) => {
    const label = getLabel(o).toLowerCase()
    const badge = getBadge(o)?.toLowerCase() ?? ''
    return label.includes(keyword) || badge.includes(keyword)
  })
})

const wrapperStyle = computed(() => ({
  width: typeof props.width === 'number' ? `${props.width}px` : props.width
}))

function toggleDropdown(): void {
  if (props.disabled) return
  const next = !dropdownOpen.value
  if (next) {
    searchKeyword.value = ''
    updatePanelPosition()
  }
  dropdownOpen.value = next
}

/** 程序化展开（供级联等父组件在上级选定后自动展开下一级，减少点击次数） */
function open(): void {
  if (props.disabled || dropdownOpen.value) return
  searchKeyword.value = ''
  updatePanelPosition()
  dropdownOpen.value = true
}

defineExpose({ open })

function bindPanelListeners(): void {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', updatePanelPosition)
  window.addEventListener('scroll', updatePanelPosition, true)
}

function unbindPanelListeners(): void {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updatePanelPosition)
  window.removeEventListener('scroll', updatePanelPosition, true)
}

function clearValue(event: MouseEvent): void {
  // 清空按钮在触发框内：阻止冒泡避免触发 toggle，同时阻断 document click 的关闭判定
  event.stopPropagation()
  emit('update:modelValue', null)
  emit('change', null)
  dropdownOpen.value = false
}

function selectOption(option: DropdownOption): void {
  if (option.disabled) return
  const value = getValue(option) as string | number | null
  if (value === props.modelValue) {
    dropdownOpen.value = false
    return
  }
  emit('update:modelValue', value)
  emit('change', value)
  dropdownOpen.value = false
}

const NAV_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']

function handleKeydown(event: KeyboardEvent): void {
  if (!NAV_KEYS.includes(event.key)) return
  const target = event.target as HTMLElement
  if (target.tagName === 'INPUT') return
  if (props.options.length === 0) return
  event.preventDefault()
  const isPrev = event.key === 'ArrowUp' || event.key === 'ArrowLeft'
  const currentIndex = props.options.findIndex((o) => getValue(o) === props.modelValue)
  let newIndex: number
  if (currentIndex === -1) {
    newIndex = isPrev ? props.options.length - 1 : 0
  } else {
    newIndex = isPrev
      ? Math.max(0, currentIndex - 1)
      : Math.min(props.options.length - 1, currentIndex + 1)
  }
  const next = props.options[newIndex]
  if (next && !next.disabled) {
    const nextValue = getValue(next)
    if (nextValue !== props.modelValue) {
      emit('update:modelValue', nextValue as string | number)
      emit('change', nextValue as string | number)
    }
  }
}

function handleClickOutside(event: MouseEvent): void {
  const target = event.target as HTMLElement | null
  if (!target?.closest('.dropdown-select') && !target?.closest('.dropdown-panel')) {
    dropdownOpen.value = false
  }
}

watch(dropdownOpen, (open) => {
  if (open) {
    bindPanelListeners()
    void scrollToSelectedOption()
  } else {
    unbindPanelListeners()
  }
})

onBeforeUnmount(() => {
  unbindPanelListeners()
})
</script>

<template>
  <div ref="wrapperRef" class="dropdown-select" :style="wrapperStyle">
    <div
      class="dropdown-select__trigger"
      :class="{
        'dropdown-select__trigger--active': dropdownOpen,
        'dropdown-select__trigger--has-value': !!selectedOption,
        'dropdown-select__trigger--disabled': disabled,
        'dropdown-select__trigger--error': error,
        'dropdown-select__trigger--clearable': clearable,
        [`dropdown-select__trigger--${size}`]: true,
        [`dropdown-select__trigger--${variant}`]: true
      }"
      tabindex="0"
      @click="toggleDropdown"
      @keydown="handleKeydown"
    >
      <span class="dropdown-select__label">
        {{ selectedOption ? getSelectedLabel(selectedOption) : placeholder }}
      </span>
      <span v-if="selectedOption && getBadge(selectedOption)" class="dropdown-select__badge">
        {{ getBadge(selectedOption) }}
      </span>
      <span
        v-if="clearable && selectedOption && !disabled"
        class="dropdown-select__clear"
        role="button"
        aria-label="清除选择"
        @click.stop="clearValue"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </span>
      <span class="dropdown-select__arrow">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </span>
    </div>

    <!-- 弹层挂到 body：Modal/表格等 overflow 祖先不再裁切候选项。
         关闭态移出 DOM（v-if + Transition），避免隐藏弹层撑出幻影滚动条。 -->
    <Teleport to="body">
      <Transition name="dropdown-panel">
        <div
          v-if="dropdownOpen"
          class="dropdown-panel dropdown-panel--portal"
          :class="`dropdown-panel--${align}`"
          :style="panelPositionStyle"
          @click.stop
        >
        <div v-if="searchable" class="dropdown-panel__search">
          <SearchInput
            v-model="searchKeyword"
            variant="unified"
            size="sm"
            :placeholder="searchPlaceholder"
            width="100%"
            :clearable="true"
          />
        </div>
        <div
          ref="listRef"
          class="dropdown-panel__list"
          :style="listMaxHeight !== null ? { maxHeight: `${listMaxHeight}px` } : undefined"
        >
          <div
            v-for="option in filteredOptions"
            :key="String(getValue(option))"
            class="dropdown-panel__option"
            :class="{
              'dropdown-panel__option--selected': getValue(option) === modelValue,
              'dropdown-panel__option--disabled': option.disabled
            }"
            @click.stop="selectOption(option)"
          >
            <span class="dropdown-panel__option-label">{{ getLabel(option) }}</span>
            <span v-if="getBadge(option)" class="dropdown-panel__option-badge">
              {{ getBadge(option) }}
            </span>
          </div>
          <div v-if="filteredOptions.length === 0" class="dropdown-panel__empty">
            {{ emptyText }}
          </div>
        </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.dropdown-select {
  position: relative;
  display: inline-flex;
}

.dropdown-select__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-1);
  // 140px 是独立使用时的设计下限；用 min() 让下限不突破容器宽度（注意：CSS 中 min-width
  // 优先于 max-width，max-width 兜底无效），窄容器（flex 收缩场景）下自动收窄，使用方无需局部覆盖
  min-width: min(140px, 100%);
  padding: 0 var(--spacing-2) 0 var(--spacing-3);
  /* /next 深色桌布混搭：跟卡片面，避免硬编码白底与浅字令牌打架 */
  background: var(--bg-card, var(--white));
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-input);
  cursor: pointer;
  transition: all var(--transition-fast);
  user-select: none;
  width: 100%;
  box-sizing: border-box;

  // 尺寸：高度/字号对齐表单控件规范，保证表单内与输入控件对齐
  &--sm {
    /* /next 有 --nx-control-h-sm=32；旧版 /app 无该令牌，回退 36 */
    height: var(--nx-control-h-sm, 36px);
    font-size: var(--text-xs);

    .dropdown-select__label {
      font-size: var(--text-xs);
    }
  }

  &--md {
    height: var(--nx-control-h-md, 48px);

    .dropdown-select__label {
      font-size: var(--text-sm);
    }
  }

  &--lg {
    height: 56px;

    .dropdown-select__label {
      font-size: var(--text-base);
    }
  }

  // 外观：outlined 与默认一致；compact 收窄内边距与圆角，用于紧凑工具条/筛选区，
  // 字号仍由 size 决定（避免同一控件两个维度互相覆盖）
  &--compact {
    padding: 0 var(--spacing-2) 0 var(--spacing-2-5);
    border-radius: var(--radius-sm);
  }

  &:hover:not(.dropdown-select__trigger--disabled) {
    border-color: var(--border-hover);
  }

  &:focus,
  &.dropdown-select__trigger--active {
    border-color: var(--primary);
    box-shadow: var(--shadow-input), 0 0 0 3px var(--blue-alpha-15);
    outline: none;
  }

  &--error {
    border-color: var(--danger);
    background: var(--danger-50);

    &:focus,
    &.dropdown-select__trigger--active {
      box-shadow: var(--shadow-input), 0 0 0 3px var(--danger-alpha-15);
    }
  }

  &--clearable {
    padding-right: var(--spacing-8);
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: var(--bg-tertiary);
  }
}

.dropdown-select__label {
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .dropdown-select__trigger:not(.dropdown-select__trigger--has-value) & {
    color: var(--text-muted);
  }
}

.dropdown-select__clear {
  margin-left: auto;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
  transition: all var(--transition-fast);

  &:hover {
    color: var(--text-primary);
    background: var(--bg-tertiary);
  }
}

.dropdown-select__badge {
  padding: 0 var(--spacing-1);
  background: var(--surface-dim);
  color: var(--text-secondary);
  border-radius: var(--radius-2xs);
  font-size: var(--text-3xs);
  white-space: nowrap;
}

.dropdown-select__arrow {
  margin-left: auto;
  color: var(--text-secondary);
  transition: transform var(--transition-fast);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .dropdown-select__trigger--active & {
    transform: rotate(180deg);
  }
}
</style>
